"use client";
import { useState, useMemo, useEffect } from "react";
import { useRouter } from "next/navigation";
import { ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { MAJOR_US_CITIES } from "@/lib/ingest/cities";
import { ALL_US_CITIES } from "@/lib/ingest/all-cities";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { motion } from "framer-motion";

interface ApplicatorsComboboxProps {
  transparentAtTop: boolean;
  scheme: "light" | "dark";
  forceWhite?: boolean; // homepage hero override for white text
  className?: string;
}

export function ApplicatorsCombobox({
  transparentAtTop,
  scheme,
  forceWhite = false,
  className,
}: ApplicatorsComboboxProps) {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  // Local input state (Command handles its own filtering but we want to drive remote state queries)
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 200);

  // Build base city dataset for fallback (when no search or offline)
  // majorSet placeholder (remove entirely until weighting needed)
  // We previously separated otherCities; now unused after simplifying logic.
  // Keeping majorSet for potential future weighting but not storing otherCities separately.
  // (allCities reserved for potential future ranking – remove unused var to satisfy lint)

  // Fetch states (filtered) via API when searching
  const statesQuery = useQuery({
    queryKey: ["applicator-states", debounced],
    enabled: !!debounced,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debounced) params.set("search", debounced);
      const res = await fetch(`/api/applicators/states?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load states");
      return (await res.json()) as {
        items: { state: string; name: string; slug: string; count: number }[];
      };
    },
  });

  // Filter cities client-side using the debounced query
  const filteredCities = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    if (!q) return [];
    return ALL_US_CITIES.filter((c) => c.name.toLowerCase().includes(q)).slice(
      0,
      50
    );
  }, [debounced]);

  // Show popular cities when no search; show filtered cities & states sections when searching
  const showSearchResults = !!debounced;

  const linkColorClasses = transparentAtTop
    ? forceWhite
      ? "text-white/80 hover:text-white"
      : scheme === "dark"
      ? "text-foreground/70 hover:text-foreground"
      : "text-black/70 hover:text-black dark:text-white/70 dark:hover:text-white"
    : "text-muted-foreground hover:text-foreground";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={`px-3 py-2 text-sm font-medium transition-colors relative group flex items-center ${linkColorClasses} ${
          className ?? ""
        }`}
        aria-label="Find applicators by city"
      >
        <span>Find Applicators</span>
        <ChevronDown className="ml-1 h-3.5 w-3.5 opacity-60 group-hover:opacity-90 transition-opacity" />
        <motion.div
          className={`absolute bottom-0 left-0 w-full h-0.5 origin-left ${
            transparentAtTop
              ? forceWhite
                ? "bg-white"
                : scheme === "dark"
                ? "bg-foreground"
                : "bg-black dark:bg-white"
              : "bg-foreground"
          }`}
          initial={{ scaleX: 0 }}
          whileHover={{ scaleX: 1 }}
          transition={{ duration: 0.2 }}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-96" sideOffset={8}>
        <Command shouldFilter={false}>
          <div className="px-3 pt-3 pb-2 border-b">
            <CommandInput
              placeholder="Search states or cities…"
              value={query}
              onValueChange={setQuery}
              className="w-full"
            />
          </div>
          <CommandList className="max-h-72 overflow-y-auto">
            <CommandEmpty className="px-3 py-2 text-sm text-muted-foreground">
              {showSearchResults ? "No matches found." : "No data."}
            </CommandEmpty>
            {showSearchResults ? (
              <>
                <CommandGroup heading="States" className="mt-1">
                  {statesQuery.isLoading && (
                    <p className="px-3 py-2 text-xs text-muted-foreground">
                      Loading states…
                    </p>
                  )}
                  {statesQuery.data?.items.slice(0, 20).map((s) => (
                    <CommandItem
                      key={s.slug}
                      value={s.name}
                      onSelect={() => {
                        setOpen(false);
                        router.push(`/applicators/${s.slug}`);
                      }}
                      className="cursor-pointer"
                    >
                      {s.name}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {s.count} installer{s.count === 1 ? "" : "s"}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandGroup heading="Cities" className="mt-2">
                  {filteredCities.length === 0 &&
                    debounced &&
                    !statesQuery.isLoading && (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        No cities match.
                      </p>
                    )}
                  <div className="grid grid-cols-2 gap-1">
                    {(() => {
                      const colSize = Math.ceil(filteredCities.length / 2);
                      const first = filteredCities.slice(0, colSize);
                      const second = filteredCities.slice(colSize);
                      return [first, second].map((col, idx) => (
                        <div key={idx} className="space-y-0.5">
                          {col.map((c) => (
                            <CommandItem
                              key={c.slug}
                              value={c.name.split(",")[0]}
                              className="cursor-pointer"
                              onSelect={() => {
                                setOpen(false);
                                const parts = c.name.split(", ");
                                const stateCode = parts[1];
                                const citySlug = c.slug.replace(
                                  /-([a-z]{2})$/i,
                                  ""
                                );
                                router.push(
                                  `/applicators/${stateCode.toLowerCase()}/${citySlug}`
                                );
                              }}
                            >
                              {c.name.split(",")[0]}
                              <span className="ml-auto text-xs text-muted-foreground">
                                {c.name.split(", ")[1]}
                              </span>
                            </CommandItem>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                </CommandGroup>
              </>
            ) : (
              <CommandGroup heading="Popular Cities" className="mt-1">
                <div className="grid grid-cols-2 gap-1">
                  {(() => {
                    const popular = MAJOR_US_CITIES.slice(0, 20);
                    const colSize = Math.ceil(popular.length / 2);
                    const first = popular.slice(0, colSize);
                    const second = popular.slice(colSize);
                    return [first, second].map((col, idx) => (
                      <div key={idx} className="space-y-0.5">
                        {col.map((c) => (
                          <CommandItem
                            key={c.slug}
                            value={c.name.split(",")[0]}
                            className="cursor-pointer"
                            onSelect={() => {
                              setOpen(false);
                              const parts = c.name.split(", ");
                              const stateCode = parts[1];
                              const citySlug = c.slug.replace(
                                /-([a-z]{2})$/i,
                                ""
                              );
                              router.push(
                                `/applicators/${stateCode.toLowerCase()}/${citySlug}`
                              );
                            }}
                          >
                            {c.name.split(",")[0]}
                            <span className="ml-auto text-xs text-muted-foreground">
                              {c.name.split(", ")[1]}
                            </span>
                          </CommandItem>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              </CommandGroup>
            )}
          </CommandList>
          <div className="border-t p-3 flex justify-between gap-2">
            <Link
              href="/applicators"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-primary hover:underline"
            >
              Browse Directory →
            </Link>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function useDebounce<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
